<template>
  <div
    class="section-selector-selected relative flex flex-col rounded-2 px-2 base min-h-full overflow-auto max-h-[calc(100vh-300px)] pb-2 h-full"
    :style="{
      border:
        !!launchDarkly.enable_selectors_dnd && isDragging && !draggingOverItemId && !draggingOverItemSeparator
          ? `1px dashed ${themeVars.primary}`
          : '1px solid transparent',
    }"
    @dragenter.prevent="handleDragEnter"
    @dragleave="handleDragLeave"
    @dragover.prevent="handleDragOver($event)"
    @drop.prevent="handleDrop"
  >
    <div class="flex justify-between items-center sticky top-0 base z-1 pa-2">
      <div class="panel-title font-600">
        {{ t('canvasUI.canvasBuilder.sectionSelectorSelected.selectedCount', { count: modelValue.length }) }}
      </div>

      <CButton
        class="pa-1 pl-2 !normal-case !tracking-normal underline"
        :disabled="!modelValue.length"
        size="tiny"
        text
        @click="$emit('clear')"
      >
        {{ t('canvasUI.canvasBuilder.sectionSelectorSelected.clearSelection') }}
      </CButton>
    </div>

    <Draggable
      v-if="modelValue?.length"
      handle=".selection-panel-list-handle"
      item-key="id"
      :model-value="modelValue"
      @dragend="clearState"
      @dragover.prevent="handleDragOver($event)"
      @update:model-value="$emit('update:modelValue', $event)"
    >
      <div v-for="(item, index) in modelValue" :key="item.id">
        <div
          v-if="!!launchDarkly.enable_selectors_dnd && index > 0"
          class="flex items-center w-full h-2"
          @dragend="clearState"
          @dragover.prevent.stop="handleDragOver($event, item.id, true)"
          @drop.prevent="handleDrop($event, item.id, index)"
          @mouseleave="clearState"
        >
          <div
            class="w-full"
            :style="{
              borderBottom:
                !!launchDarkly.enable_selectors_dnd && !draggingOverItemId && draggingOverItemSeparator === item.id
                  ? `1px dashed ${themeVars.primary}`
                  : '1px solid transparent',
            }"
          />
        </div>
        <div
          class="selection-panel-list-item group flex items-center h-14 rounded-2 mr-1 relative"
          :class="{ 'mt-2': !!index && !launchDarkly.enable_selectors_dnd }"
          :style="{
            border:
              !!launchDarkly.enable_selectors_dnd && !draggingOverItemSeparator && draggingOverItemId === item.id
                ? `1px dashed ${themeVars.primary}`
                : '1px solid transparent',
          }"
          @dragend="clearState"
          @dragover.prevent.stop="handleDragOver($event, item.id)"
          @drop.prevent.stop="handleDrop($event, item.id, index)"
          @mouseleave="clearState"
        >
          <CIcon
            class="selection-panel-list-handle px-1 cursor-move flex-shrink-0"
            fa-type="fas"
            icon="grip-dots-vertical"
            size="20px"
          />

          <div
            v-tooltip="item.is_draft ? t('canvasUI.canvasBuilder.sectionSelectorSelected.draftSectionTooltip') : ''"
            class="flex-shrink-0 h-16 w-16 flex items-center justify-center relative"
          >
            <CThumbnail
              v-if="index < MAX_ITEMS_TO_DISPLAY_THUMBNAIL"
              :class="{ 'opacity-50': item.is_draft }"
              :height="40"
              :item="item"
              :preview-disabled="false"
              :themeVars="themeVars"
              :thumbnailUrl="item.thumbnail_url ?? item.metadata?.thumbnail"
              :width="60"
            />

            <CIcon
              v-if="item.is_draft"
              class="color-primary3 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
              fixed-width
              icon="eye-slash"
              size="12"
            />
          </div>

          <div
            class="mx-2 w-full overflow-hidden title text-ellipsis whitespace-nowrap font-700"
            :class="{ 'opacity-50': item.is_draft }"
          >
            {{ item.name }}
          </div>

          <div
            v-if="launchDarkly.enable_section_selector_edit_section && isAdmin"
            v-tooltip="t('canvasUI.canvasBuilder.sectionSelectorSelected.openSectionInEditMode')"
            class="absolute right-12 w-8 h-8 flex items-center justify-center cursor-pointer rounded-full group-hover:opacity-100 opacity-0 transition-opacity bg-primary6"
            @click.stop="navigateToSectionEdit(item)"
          >
            <CIcon icon="arrow-up-right-from-square" size="14px" />
          </div>

          <CIcon
            class="flex-shrink-0 rounded-full cursor-pointer bg-primary5 mr-2 pa-2"
            :color="themeVars.primary"
            icon="close"
            size="14px"
            @click="$emit('remove', item)"
          />
        </div>
      </div>
    </Draggable>
    <div
      v-else
      class="absolute z-0 top-0 left-0 w-full h-full flex flex-col gap-4 items-center justify-center color-primary3"
    >
      <CIcon icon="face-meh-blank" :size="32" />
      <span class="text-size-xs">{{ t('canvasUI.canvasBuilder.sectionSelectorSelected.nothingSelectedYet') }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref, onUnmounted, type ComputedRef } from 'vue'
import { VueDraggable as Draggable } from 'vue-draggable-plus'
import { useI18n } from 'vue-i18n'

import CIcon from '@lib/components/CIcon/CIcon.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import CThumbnail from '@lib/components/CThumbnail/CThumbnail.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import useCanvas from '@canvas-builder/composables/useCanvas'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { Canvas, CanvasRetrieve } from '@openapi'

const emit = defineEmits<{
  (e: 'update:modelValue', items: Canvas[]): void
  (e: 'remove', item: Canvas): void
  (e: 'clear'): void
  (e: 'drop', dropOnItemId?: Canvas['id'], index?: number): void
}>()

defineProps<{
  modelValue: Canvas[]
}>()

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const openSection = inject<{
  (canvasId: CanvasRetrieve['id'], { editMode }: { editMode?: boolean }): void
}>('open-section')

const MAX_ITEMS_TO_DISPLAY_THUMBNAIL =
  launchDarkly.value.canvas_section_selector_selected_items_with_thumbnail_size || 0
const { t } = useI18n()
const { isAdmin } = useCanvas()
const themeVars = useThemeVars()

const draggingOverItemId = ref<Canvas['id'] | null>(null)
const draggingOverItemSeparator = ref<Canvas['id'] | null>(null)
const isDragging = ref(false)
let dragOverTimeout: ReturnType<typeof setTimeout> | null = null

function navigateToSectionEdit(item: Canvas) {
  openSection?.(item.id.toString(), { editMode: true })
}

function clearState() {
  draggingOverItemSeparator.value = null
  draggingOverItemId.value = null
  isDragging.value = false
}

function handleDrop(event: DragEvent, droppedOnItemId?: Canvas['id'], index?: number) {
  emit('drop', droppedOnItemId, index)
  clearState()
}

function handleDragOver(event: DragEvent, droppedOnItemId?: Canvas['id'], isSeparator?: boolean) {
  isDragging.value = true

  if (isSeparator) {
    draggingOverItemId.value = null
    draggingOverItemSeparator.value = droppedOnItemId ?? null
  } else {
    draggingOverItemId.value = droppedOnItemId ?? null
    draggingOverItemSeparator.value = null
  }

  if (dragOverTimeout) {
    clearTimeout(dragOverTimeout)
  }

  // Set a new timeout to clear draggingOverItemId after some inactivity
  dragOverTimeout = setTimeout(() => {
    if (!isDragging.value) {
      clearState()
    }
  }, 250)
}

function handleDragEnter() {
  isDragging.value = true
}

function handleDragLeave(event: DragEvent) {
  const relatedTarget = event.relatedTarget as Node
  if (!(event.currentTarget as Node)?.contains(relatedTarget)) {
    isDragging.value = false
    clearState()
  }
}

onUnmounted(() => {
  if (dragOverTimeout) {
    clearTimeout(dragOverTimeout)
  }
})
</script>

<style scoped lang="scss">
.section-selector-selected {
  border: 1px solid v-bind('themeVars.primary5');
}
.panel-title {
  color: v-bind('themeVars.text');
  font-size: 15px;
  line-height: 28px;
  letter-spacing: 0.15px;
}

.base {
  background-color: v-bind('themeVars.base');
}

.selection-panel-list-item {
  background: v-bind('themeVars.primary6');
}

.selection-panel-list-handle {
  color: v-bind('themeVars.primary3');
}

.title {
  font-size: 15px;
  color: v-bind('themeVars.text');
  letter-spacing: 0.4px;
}

.description {
  color: v-bind('themeVars.primary3');
}
</style>
